import { DEFAULT_TIMEZONE, assistantProposalView, type AdminAssistantConversation, type AdminAssistantTurn, type AssistantTopic } from "@teamna/shared";
import { useParams } from "@tanstack/react-router";
import { DataTable, ErrorState, PageHeader, ShieldIcon, SkeletonList, type DataTableColumn } from "@teamna/ui/components/index.ts";
import {
  AssistantFacts,
  AssistantProposal,
  AssistantResult,
  AssistantShare,
  AssistantTable,
  AssistantText,
  AssistantToolCalls,
  ChatStatusPill,
  UserBubble,
} from "@teamna/ui/owner/assistant/assistant-blocks.tsx";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { useAdminBusiness, useAssistantConversationForStaff } from "../lib/cc-api.ts";
import { CARD_KIND_WORDS, PLAN_WORDS, ROLE_WORDS, TOOL_WORDS, TOPIC_WORDS, formatUsd } from "./assistant-format.ts";
import { OutcomePill, RatingView } from "./assistant-review.tsx";

/**
 * One AI Assistant chat, read only (`/assistant/$conversationId`): every turn as the person saw it,
 * with what the screen hides under each answer (topic, model, tokens, cost, rating, reason and
 * note), and every plan with its status. The blocks are the Desk components, so the chat looks the
 * same. Nothing here writes to the chat; the read itself writes an audit row with the staff email.
 */

const BACK = { to: "/assistant", label: "AI Assistant" };

type Action = AdminAssistantConversation["actions"][number];

function planColumns(timezone: string): DataTableColumn<Action>[] {
  return [
    { id: "title", header: "Plan", cell: (row) => <span dir="auto">{row.title}</span> },
    { id: "tool", header: "Change", cell: (row) => TOOL_WORDS[row.tool] },
    { id: "version", header: "Version", align: "end", cell: (row) => <span className="tabular-nums">{row.version}</span> },
    { id: "status", header: "Status", cell: (row) => PLAN_WORDS[row.status] },
    {
      id: "decided",
      header: "Decided",
      cell: (row) => (row.decidedAt ? <span className="whitespace-nowrap">{formatDateTime(row.decidedAt, timezone)}</span> : <span className="text-ink-3">Not yet</span>),
    },
  ];
}

export function AssistantChatReviewScreen() {
  const { conversationId } = useParams({ from: "/staff/assistant/$conversationId" });
  const chat = useAssistantConversationForStaff(conversationId);
  // Tables with dates read in the business's time zone, like in Desk.
  const business = useAdminBusiness(chat.data?.businessId ?? "");
  const timezone = business.data?.timezone ?? DEFAULT_TIMEZONE;

  if (chat.isLoading) {
    return (
      <>
        <PageHeader title="Chat" back={BACK} />
        <SkeletonList rows={4} />
      </>
    );
  }
  if (!chat.data) {
    return (
      <>
        <PageHeader title="Chat" back={BACK} />
        <ErrorState error={chat.error} onRetry={() => void chat.refetch()} />
      </>
    );
  }
  const data = chat.data;
  const asker = `${data.asker.name ?? "No name"}, ${ROLE_WORDS[data.asker.role]}`;

  return (
    <>
      <PageHeader title={data.title} description={`${data.businessName} · ${asker}`} back={BACK} />
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <ChatStatusPill status={data.status} />
        <span className="text-[13px] text-ink-2">
          {data.cardKind ? CARD_KIND_WORDS[data.cardKind] : "A question"}, {formatDateTime(data.createdAt, timezone)}
        </span>
      </div>
      <p className="mb-6 flex items-center gap-2 text-[13px] text-ink-2">
        <ShieldIcon size={16} />
        Opening this chat is recorded with your email.
      </p>

      <section aria-label="Chat" className="mb-8 max-w-[760px] rounded-card border border-line bg-paper px-5 shadow-card">
        <ol className="space-y-4 py-4">
          {data.turns.map((turn, index) => (
            <li key={turn.id}>
              <TurnView turn={turn} topic={topicBefore(data.turns, index)} conversation={data} timezone={timezone} />
            </li>
          ))}
        </ol>
      </section>

      <h2 className="mb-2 text-[17px]">Plans</h2>
      <DataTable
        columns={planColumns(timezone)}
        rows={data.actions}
        rowKey={(row) => row.id}
        caption="Plans"
        density="compact"
        countLabel={(count) => (count === 1 ? "1 plan" : `${count} plans`)}
        empty={{ title: "No plans", message: "AI Assistant proposed no change in this chat." }}
      />
    </>
  );
}

/** The topic of the prompt that an answer replies to: the last prompt before it. */
function topicBefore(turns: AdminAssistantTurn[], index: number): AssistantTopic | null {
  for (let at = index; at >= 0; at -= 1) {
    if (turns[at]!.role === "user") return turns[at]!.topic;
  }
  return null;
}

function TurnView({
  turn,
  topic,
  conversation,
  timezone,
}: {
  turn: AdminAssistantTurn;
  topic: AssistantTopic | null;
  conversation: AdminAssistantConversation;
  timezone: string;
}) {
  if (turn.role === "user") return <UserBubble text={turn.text} />;
  if (turn.role === "event") return <p className="text-center text-[12.5px] text-ink-2">{turn.text}</p>;

  return (
    <div>
      {turn.text ? <AssistantText text={turn.text} error={turn.outcome === "error"} /> : null}
      {turn.blocks.map((block, index) => {
        // A chart draws in the card of the table right after it.
        if (block.type === "chart") return null;
        if (block.type === "table") {
          const before = turn.blocks[index - 1];
          return <AssistantTable key={`${block.title}|${block.caption ?? ""}`} block={block} chart={before?.type === "chart" ? before : undefined} timezone={timezone} />;
        }
        if (block.type === "facts") return <AssistantFacts key={block.title} block={block} />;
        if (block.type === "result") return <AssistantResult key={block.text} block={block} />;
        if (block.type === "share") return <AssistantShare key={block.title} block={block} />;
        // Read only: no onPick, so the proposal has no "Pick this", and there is no decision bar.
        if (block.type === "proposal") return <AssistantProposal key={block.actionId} block={block} view={assistantProposalView(conversation, block)} />;
        return null;
      })}
      <AssistantToolCalls calls={turn.toolCalls} />
      <AnswerFacts turn={turn} topic={topic} />
    </div>
  );
}

/** What the Desk screen hides: how the answer ended, the topic, the model, the tokens, the cost and the rating. */
function AnswerFacts({ turn, topic }: { turn: AdminAssistantTurn; topic: AssistantTopic | null }) {
  const parts = [
    `Topic ${topic ? TOPIC_WORDS[topic] : "none"}`,
    `Model ${turn.model ?? "none"}`,
    `${turn.inputTokens} in, ${turn.outputTokens} out tokens`,
    formatUsd(turn.costMicros),
  ];
  return (
    <div className="mt-2 space-y-1 rounded-lg border border-line bg-sunken/60 px-3 py-2 text-[12.5px] text-ink-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <OutcomePill outcome={turn.outcome} />
        <span>{parts.join(" · ")}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-semibold">Rating</span>
        <RatingView rating={turn.rating ?? null} reason={turn.ratingReason} />
      </div>
      {turn.ratingNote ? (
        <p dir="auto" className="whitespace-pre-wrap text-ink">
          <span className="font-semibold text-ink-2">Note: </span>
          {turn.ratingNote}
        </p>
      ) : null}
    </div>
  );
}
